import React, { Fragment } from 'react'
import {
  Container,
  Grid,
  Typography,
  TextField,
  Box,
  Paper,
  Tooltip,
  Divider,
  Button
} from '@mui/material'
import { Save, Info } from '@mui/icons-material'
import { Pagination } from '../../../../components/Pagination'
import { Loading } from '../../../../components/UI'
import { WorkOrderInboundSkuAdjust } from '../Entities/InboundAdjust'
import { formatExpirationDate } from '../../../../utils/dateHelper'

interface GridElementModalDetailProps {
  loading: boolean
  items: WorkOrderInboundSkuAdjust[]
  rowQuantities: RowQuantities
  rowComments: RowComments
  skuAdjust: any
  onQuantityChange: (
    rowIndex: number,
    fieldType: 'missingQty' | 'qualityQty',
    value: string
  ) => void
  onCommentChange: (rowIndex: number, value: string) => void
  onSaveLine: (rowIndex: number, asnDetailId: number) => void
  onPaginationChange: (page: number, pageSize: number) => void
  totalItems: number
  paginationState: { page: number; pageSize: number }
}

type RowQuantities = Record<number, { missingQty: number; qualityQty: number }>
type RowComments = Record<number, string>

export const GridElementModalDetailNew: React.FC<
  GridElementModalDetailProps
> = ({
  loading,
  items,
  rowQuantities,
  rowComments,
  skuAdjust,
  onQuantityChange,
  onCommentChange,
  onSaveLine,
  onPaginationChange,
  totalItems,
  paginationState
}) => {
  const hasBatch = items.some((i) => i.batchId > 0)

  const renderHeader = () => (
    <Fragment>
      <Grid
        container
        spacing={2}
        style={{
          alignItems: 'center',
          color: 'text.secondary',
          padding: '8px 0',
          marginLeft: '10px'
        }}
      >
        <Grid item xs={3}>
          <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
            Producto
          </Typography>
        </Grid>
        {hasBatch && (
          <Grid item xs={1}>
            <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
              Lote/Fecha Venc
            </Typography>
          </Grid>
        )}
        <Grid item xs={0.75}>
          <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
            Unidades
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <Box display='flex' alignItems='center'>
            <Tooltip title='Se declara que existen unidades no encontradas las cuales se enviarán a ubicación de diferencia.'>
              <Info fontSize='small' style={{ marginRight: '4px' }} />
            </Tooltip>
            <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
              Declarar Faltante
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Box display='flex' alignItems='center'>
            <Tooltip title="Se identifica un producto con daño por lo que se enviará a calidad, gestionar desde 'Alertas de inventario'.">
              <Info fontSize='small' style={{ marginRight: '4px' }} />
            </Tooltip>
            <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
              Enviar a Calidad
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={2}>
          <Typography style={{ fontSize: '12px', fontWeight: 'bold' }}>
            Comentario
          </Typography>
        </Grid>
        <Grid item xs={1}></Grid>
      </Grid>
      <Divider style={{ margin: '8px 0' }} />
    </Fragment>
  )

  if (loading) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <Loading />
      </div>
    )
  }

  return (
    <Fragment>
      <Container maxWidth='xl' style={{ padding: 0 }}>
        <Paper elevation={0} style={{ padding: '12px 16px', marginBottom: '2px', backgroundColor: '#e8e8e8', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          {hasBatch ? (
            <Typography
              variant='subtitle2'
              align='left'
              style={{
                fontWeight: 'bold',
                margin: 0,
                color: '#666'
              }}
            >
              Detalle por Lotes
            </Typography>
          ) : (
            <Typography
              variant='subtitle2'
              align='left'
              style={{
                fontWeight: 'bold',
                margin: 0,
                color: '#666'
              }}
            >
              Detalle por Producto
            </Typography>
          )}
        </Paper>

        <Paper elevation={1} style={{ padding: '12px', backgroundColor: '#ffffff' }}>
          <Box style={{ border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden' }}>
            {renderHeader()}

            {items.length > 0 ? (
              items.map((producto, index) => {
              const isDisabled =
                (producto.missingQty !== 0 && producto.missingQty != null) ||
                (producto.reductionQty !== 0 && producto.reductionQty != null)

              return (
                <Fragment key={producto.batch + index.toString() || index}>
                  {index > 0 && <Divider />}
                  <Grid
                    container
                    spacing={2}
                    style={{
                      alignItems: 'center',
                      padding: '12px 0',
                      marginLeft: '10px'
                    }}
                  >
                    <Grid item xs={3}>
                      <Typography style={{ fontSize: '13px' }}>
                        {skuAdjust?.sku || producto.sku || 'N/A'}
                      </Typography>
                    </Grid>

                    {hasBatch && (
                      <Grid item xs={1}>
                        <Typography
                          style={{
                            fontSize: '13px',
                            color:
                              producto.batch || producto.expirationDate
                                ? 'inherit'
                                : 'gray',
                            fontStyle:
                              producto.batch || producto.expirationDate
                                ? 'normal'
                                : 'italic'
                          }}
                        >
                          {producto.batch ||
                            formatExpirationDate(
                              producto.expirationDate || '',
                              'dd-MM-yyyy'
                            ) ||
                            'Sin Lote'}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item xs={0.75}>
                      <Typography style={{ fontSize: '13px' }}>
                        {producto.receptionQty}
                      </Typography>
                    </Grid>

                    <Grid item xs={1}>
                      <TextField
                        type='number'
                        size='small'
                        variant='outlined'
                        disabled={isDisabled}
                        value={
                          producto.missingQty ||
                          rowQuantities[index]?.missingQty ||
                          0
                        }
                        onChange={(e) =>
                          onQuantityChange(index, 'missingQty', e.target.value)
                        }
                        inputProps={{
                          min: 0,
                          style: { textAlign: 'center' }
                        }}
                        style={{ width: '100%' }}
                      />
                    </Grid>

                    <Grid item xs={1}>
                      <TextField
                        type='number'
                        size='small'
                        variant='outlined'
                        disabled={isDisabled}
                        value={
                          producto.reductionQty ||
                          rowQuantities[index]?.qualityQty ||
                          0
                        }
                        onChange={(e) =>
                          onQuantityChange(index, 'qualityQty', e.target.value)
                        }
                        inputProps={{
                          min: 0,
                          style: { textAlign: 'center' }
                        }}
                        style={{ width: '100%' }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <TextField
                        type='text'
                        size='small'
                        variant='outlined'
                        disabled={isDisabled}
                        placeholder='Comentario...'
                        value={producto.comment || rowComments[index] || ''}
                        onChange={(e) => onCommentChange(index, e.target.value)}
                        fullWidth
                        inputProps={{ maxLength: 200 }}
                      />
                    </Grid>

                    <Grid item xs={1}>
                      <Button
                        color='primary'
                        variant='contained'
                        disabled={isDisabled}
                        onClick={() =>
                          onSaveLine(index, producto.asnDetailId || 0)
                        }
                        startIcon={<Save />}
                        sx={{
                          fontSize: '0.75rem',
                          padding: '8px 12px',
                          textTransform: 'none',
                          height: '100%',
                          minHeight: '40px',
                          borderRadius: '12px'
                        }}
                      >
                        Guardar
                      </Button>
                    </Grid>
                  </Grid>
                </Fragment>
              )
            })
            ) : (
              <Box style={{ padding: '32px', textAlign: 'center' }}>
                <Typography variant='body2'>
                  No hay lotes pendientes para este producto.
                </Typography>
              </Box>
            )}
          </Box>

          {totalItems > paginationState.pageSize && (
            <Box style={{ padding: '16px', borderTop: '1px solid #e0e0e0' }}>
              <Pagination
                pagination={{
                  totalItem: totalItems,
                  page: paginationState.page,
                  pageSize: paginationState.pageSize
                }}
                onPaginationChange={onPaginationChange}
              />
            </Box>
          )}
        </Paper>
      </Container>
    </Fragment>
  )
}
